/**
 * The overview's growth chart as numbers: which weights it shows, where each
 * falls on the WHO curves, and its axes. The WHO module comes in as an
 * argument (`useGrowth` loads it), so this file imports only its types and
 * the table stays out of the first download (see `lib/growth.ts`).
 */
import {
  GRAMS_PER_OZ,
  GROWTH_MAX_DAYS,
  type GrowthBaby,
  growthAge,
  localCalendar,
  type NoGrowthReason,
  percentileText,
  type Sex,
  type WeightUnit,
} from "@babia/checks";
import type { PercentileLine, WeightReading } from "@babia/checks/growth";
import type { EntryLike } from "./entry-text.ts";
import type { Growth } from "./growth.ts";
import { mergeHistory } from "./overview.ts";
import { monthDay } from "./time.ts";

/** A weight with a number, as `/entries/weights` gives it. */
export interface StoredWeight {
  id: string;
  babyId: string;
  at: Date;
  grams: number;
}

/**
 * Every weight with a number, oldest first: the live list's weight notes for
 * the last two days over the stored weights, so a weight another parent logs,
 * fixes, or deletes shows at once (as `mergeHistory` does for the charts).
 */
export function weightPoints(
  live: readonly EntryLike[] | undefined,
  stored: readonly StoredWeight[] | undefined,
  now: Date,
): StoredWeight[] {
  const recent = live?.flatMap((e) =>
    e.type === "note" && e.weightGrams != null
      ? [{ id: e.id, babyId: e.babyId, at: e.at, grams: e.weightGrams }]
      : [],
  );
  return mergeHistory(recent, stored, now).sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** One weight in the Growth section: its row in the table, maybe a point on the chart. */
export interface GrowthRow {
  id: string;
  at: Date;
  grams: number;
  /** The phone's day it was weighed. */
  date: string;
  /**
   * Its place on the WHO curves, or why it has none. The reason needs only the
   * baby's dates and sex, so it shows even when the curves fail to load. Null
   * while they load, for a weight that will have a place on them.
   */
  reading: WeightReading | { reason: NoGrowthReason } | null;
}

/** A row with a place on the curves: a point on the chart. */
export type PlottedRow = GrowthRow & { reading: WeightReading };

export function isPlotted(row: GrowthRow): row is PlottedRow {
  return row.reading !== null && !("reason" in row.reading);
}

/** One baby's weights, oldest first, each read on the WHO curves once they load. */
export function growthRows(
  points: readonly StoredWeight[],
  baby: GrowthBaby & { id: string },
  growth: Growth | null,
): GrowthRow[] {
  return points
    .filter((p) => p.babyId === baby.id)
    .map((p) => {
      const date = localCalendar.isoDate(p.at);
      const age = growthAge(baby, date);
      return {
        id: p.id,
        at: p.at,
        grams: p.grams,
        date,
        reading:
          "reason" in age
            ? { reason: age.reason }
            : growth
              ? growth.weightReading(baby, p.grams, date)
              : null,
      };
    });
}

/** The average month, in days, as the WHO tables count it. */
export const DAYS_PER_MONTH = 30.4375;

/** Day `months` months after birth, within the WHO table. */
export function monthDays(months: number): number {
  return Math.min(GROWTH_MAX_DAYS, Math.round(months * DAYS_PER_MONTH));
}

/** The chart's x axis: its last day and its ticks, in months. */
export interface GrowthSpan {
  months: number;
  end: number;
  ticks: number[];
}

const SPANS = [
  { months: 6, every: 1 },
  { months: 12, every: 2 },
  { months: 24, every: 3 },
];

/**
 * The shortest span (6, 12, or 24 months) that leaves about a month after
 * the latest point, so the newest weight never sits on the right edge.
 */
export function growthSpan(maxDay: number): GrowthSpan {
  const span = SPANS.find((s) => maxDay <= monthDays(s.months - 1)) ?? SPANS[SPANS.length - 1]!;
  const ticks: number[] = [];
  for (let m = 0; m <= span.months; m += span.every) ticks.push(m);
  return { months: span.months, end: monthDays(span.months), ticks };
}

export const GRAMS_PER_LB = 16 * GRAMS_PER_OZ;

/** The chart's y axis, in grams, with ticks in the household's unit. */
export interface WeightScale {
  lo: number;
  hi: number;
  ticks: { grams: number; label: string }[];
}

const STEPS: Record<WeightUnit, number[]> = { lb: [1, 2, 5, 10], kg: [0.5, 1, 2, 5] };
/** At most this many gaps between ticks. */
const MAX_GAPS = 8;

/**
 * From the 2nd-percentile curve at birth to the 98th at the span's end, for
 * every sex shown, widened to hold every point, then out to whole ticks.
 */
export function weightScale(
  growth: Growth,
  sexes: readonly Sex[],
  end: number,
  grams: readonly number[],
  unit: WeightUnit,
): WeightScale {
  const low = sexes.map((s) => growth.curveGrams(s, 0, growth.PERCENTILE_Z[2]) ?? Infinity);
  const high = sexes.map((s) => growth.curveGrams(s, end, growth.PERCENTILE_Z[98]) ?? 0);
  const per = unit === "kg" ? 1000 : GRAMS_PER_LB;
  // With no sex and no weight (no caller does that), the scale starts at 0.
  const min = Math.min(...low, ...grams) / per;
  const lo = Number.isFinite(min) ? min : 0;
  const max = Math.max(...high, ...grams) / per;
  const hi = Number.isFinite(max) ? max : lo;
  const steps = STEPS[unit];
  const step =
    steps.find((s) => Math.ceil(hi / s) - Math.floor(lo / s) <= MAX_GAPS) ?? steps.at(-1)!;
  const first = Math.floor(lo / step);
  // At least one gap, so the chart never divides by zero.
  const last = Math.max(first + 1, Math.ceil(hi / step));
  const ticks: WeightScale["ticks"] = [];
  for (let i = first; i <= last; i++) {
    const value = Number((i * step).toFixed(1));
    ticks.push({ grams: value * per, label: `${value}` });
  }
  return { lo: first * step * per, hi: last * step * per, ticks };
}

/** Days and grams along one percentile curve, from birth to `end`, about `count` steps. */
export function curvePoints(
  growth: Growth,
  sex: Sex,
  z: number,
  end: number,
  count: number,
): [day: number, grams: number][] {
  const points: [number, number][] = [];
  let last = -1;
  for (let i = 0; i <= count; i++) {
    const day = Math.round((i * end) / count);
    if (day === last) continue;
    last = day;
    const grams = growth.curveGrams(sex, day, z);
    if (grams !== null) points.push([day, grams]);
  }
  return points;
}

/** The order percentile labels claim the chart's right edge in. */
const LABEL_ORDER: readonly PercentileLine[] = [50, 2, 98, 25, 75, 10, 90, 5, 95];

/** Which curves get a label at the right edge: none closer than `minGap` px to another. */
export function spacedLabels(
  lines: readonly { line: PercentileLine; y: number }[],
  minGap: number,
): Set<PercentileLine> {
  const kept: { line: PercentileLine; y: number }[] = [];
  for (const line of LABEL_ORDER) {
    const at = lines.find((l) => l.line === line);
    if (at && kept.every((k) => Math.abs(k.y - at.y) >= minGap)) kept.push(at);
  }
  return new Set(kept.map((k) => k.line));
}

/** The index of the x nearest `x`; the later one on a tie. -1 for none. */
export function nearestPoint(xs: readonly number[], x: number): number {
  let best = -1;
  for (let i = 0; i < xs.length; i++)
    if (best < 0 || Math.abs(xs[i]! - x) <= Math.abs(xs[best]! - x)) best = i;
  return best;
}

/** "Oct 12", with the year for a weight from another year. */
export function weighDay(at: Date, now: Date): string {
  const year = at.getFullYear();
  return year === now.getFullYear() ? monthDay(at) : `${monthDay(at)}, ${year}`;
}

/** "49th", "under the 1st": a percentile under a Percentile heading. */
export function percentileCell(percentile: number): string {
  return percentileText(percentile).replace(/ percentile$/, "");
}

/** "49th percentile (corrected age)": a point's percentile, as said under the chart. */
export function readingWords(reading: WeightReading): string {
  return `${percentileText(reading.percentile)}${reading.corrected ? " (corrected age)" : ""}`;
}
